import { computed, ref, type ComputedRef } from 'vue';
import type { CommandBuilder } from './command-builder';
import type { InfrastructureCommand } from './infrastructure-command';

export class RestCommandBuilder<
  Result,
  Args extends Record<string, unknown>,
  Mapped = Result,
  Error = { message: string },
> implements CommandBuilder<Result, Args, Mapped, Error> {
  static fromFetch<T, A extends Record<string, unknown>>(
    fetchFn: () => Promise<T>,
  ): RestCommandBuilder<T, A, void> {
    const voidMapper: () => void = () => undefined;
    return new RestCommandBuilder(fetchFn, voidMapper);
  }

  private fetchingFn: (fetching: ComputedRef<boolean>) => ComputedRef<boolean>;

  // its not pretty but this constructor is private
  // eslint-disable-next-line max-params
  private constructor(
    private fetchFn: () => Promise<Result>,
    private mapper: (result: Result) => Mapped,
    private sideeffect?: (args: {
      result: Result;
      reject: (reason: unknown) => void;
    }) => void,
    private errorMapper?: (error: unknown) => Error,
  ) {
    this.fetchingFn = (fetching: ComputedRef<boolean>) => fetching;
  }

  withMapper<M>(
    mapper: (result: Result) => M,
  ): RestCommandBuilder<Result, Args, M> {
    return new RestCommandBuilder<Result, Args, M>(
      this.fetchFn,
      mapper,
      this.sideeffect,
    );
  }

  withErrorMapper<E>(
    errorMapper: (error: unknown) => E,
  ): RestCommandBuilder<Result, Args, Mapped, E> {
    return new RestCommandBuilder(
      this.fetchFn,
      this.mapper,
      this.sideeffect,
      errorMapper,
    );
  }

  withFetching(
    fetchingFn: (fetching: ComputedRef<boolean>) => ComputedRef<boolean>,
  ): this {
    this.fetchingFn = fetchingFn;
    return this;
  }

  withSideeffect(
    sideeffect: (args: {
      result: Result;
      reject: (reason: unknown) => void;
    }) => void,
  ): this {
    this.sideeffect = sideeffect;
    return this;
  }

  build(): InfrastructureCommand<Mapped, Error> {
    const _fetching = ref<boolean>(false);
    const _error = ref<Error | null>(null);
    const run = () => {
      _fetching.value = true;
      return new Promise<Mapped>((resolve, reject) => {
        try {
          this.fetchFn()
            .then((result) => {
              this.sideeffect?.({ result, reject });
              const mapped = this.mapper(result);
              resolve(mapped);
            })
            .catch((error: unknown) => {
              _error.value = error;
              reject(error);
            })
            .finally(() => (_fetching.value = false));
        } catch (e) {
          reject(e);
        }
      });
    };

    const error = computed<Error | null>(() => {
      if (!_error.value) {
        return null;
      }
      return this.errorMapper?.(_error.value) ?? null;
    });

    return {
      fetching: computed(
        () => this.fetchingFn(computed(() => _fetching.value)).value,
      ),
      error,
      run,
    };
  }
}
